/* ------------------------------------------------------------------
   Corbro sheet calculator

   Sheets are cut to fit and offcuts are reused, so the sheet count is
   driven by the area to cover rather than by fitting whole sheets into
   the floor. Everything is counted in units of the small (square)
   sheet: the large sheet is two units and costs less per square metre,
   so an order carries at most one small sheet.
------------------------------------------------------------------- */

(function () {
  const UNIT_FACTORS = { mm: 1, cm: 10, m: 1000 };
  const MAX_SIDE_MM = 30000;
  const SQ_M = 1000000;

  function parseSheet(variant) {
    const numbers = (String(variant.title).match(/\d+(?:\.\d+)?/g) || []).map(Number);
    const sides = numbers.filter((n) => n >= 100);
    if (sides.length < 2) return null;

    const thickness = numbers.find((n) => n > 0 && n < 100);
    const width = sides[0];
    const height = sides[1];

    return {
      id: variant.id,
      price: variant.price,
      available: variant.available !== false,
      width: width,
      height: height,
      area: width * height,
      label: [width, height, thickness].filter(Boolean).join(' × ') + ' mm',
    };
  }

  function formatMoney(cents, moneyFormat) {
    const parts = (cents / 100).toFixed(2).split('.');
    const amount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    if (!moneyFormat) return '$' + amount;
    return moneyFormat.replace(/\{\{[^}]*\}\}/g, amount);
  }

  function formatArea(areaMm2) {
    return (areaMm2 / SQ_M).toFixed(2) + ' m²';
  }

  /* Fewest sheets that cover `area`, favouring the large sheet. */
  function planSheets(area, sheets) {
    const sorted = sheets.slice().sort((a, b) => b.area - a.area);
    const large = sorted[0];
    const small = sorted[sorted.length - 1];

    if (large === small) {
      return [{ sheet: large, quantity: Math.ceil(area / large.area - 1e-6) }];
    }

    const unitsPerLarge = Math.max(1, Math.round(large.area / small.area));
    const units = Math.ceil(area / small.area - 1e-6);

    let largeCount = Math.floor(units / unitsPerLarge);
    let smallCount = units - largeCount * unitsPerLarge;

    // One more large sheet covers more for less than the leftover small sheets.
    if (smallCount > 0 && smallCount * small.price >= large.price) {
      largeCount += 1;
      smallCount = 0;
    }

    return [
      { sheet: large, quantity: largeCount },
      { sheet: small, quantity: smallCount },
    ].filter((line) => line.quantity > 0);
  }

  class SheetCalculator {
    constructor(root) {
      this.root = root;
      this.compact = root.hasAttribute('data-cbc-compact');
      this.config = this.readConfig();
      this.sheets = (this.config.sheets || []).map(parseSheet).filter(Boolean);
      this.availableSheets = this.sheets.filter((sheet) => sheet.available);

      this.lengthInput = root.querySelector('[data-cbc-length]');
      this.widthInput = root.querySelector('[data-cbc-width]');
      this.unitSelect = root.querySelector('[data-cbc-unit]');
      this.placeholder = root.querySelector('[data-cbc-placeholder]');
      this.errorEl = root.querySelector('[data-cbc-error]');
      this.summary = root.querySelector('[data-cbc-summary]');
      this.headline = root.querySelector('[data-cbc-headline]');
      this.linesEl = root.querySelector('[data-cbc-lines]');
      this.totalEl = root.querySelector('[data-cbc-total]');
      this.metaEl = root.querySelector('[data-cbc-meta]');
      this.addButton = root.querySelector('[data-cbc-add]');
      this.feedback = root.querySelector('[data-cbc-feedback]');
      this.defaultButtonLabel = this.addButton ? this.addButton.textContent.trim() : 'Add to cart';

      this.plan = [];

      if (!this.lengthInput || !this.widthInput || !this.unitSelect) return;

      [this.lengthInput, this.widthInput].forEach((input) => {
        input.addEventListener('input', () => this.update());
      });
      this.unitSelect.addEventListener('change', () => this.update());
      if (this.addButton) this.addButton.addEventListener('click', () => this.addToCart());

      this.update();
    }

    readConfig() {
      const script = this.root.querySelector('[data-cbc-config]');
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return { sheets: [] };
      }
    }

    toMillimetres(value) {
      const factor = UNIT_FACTORS[this.unitSelect.value] || 1;
      return Math.round(value * factor);
    }

    update() {
      const length = parseFloat(this.lengthInput.value);
      const width = parseFloat(this.widthInput.value);

      if (!this.availableSheets.length) {
        this.showError(
          this.sheets.length
            ? 'Sheets are out of stock right now. Please get in touch and we will let you know when they land.'
            : 'The calculator could not load our sheet sizes. Please refresh the page or contact us.'
        );
        return;
      }

      if (!(length > 0) || !(width > 0)) {
        this.showPlaceholder();
        return;
      }

      const lengthMm = this.toMillimetres(length);
      const widthMm = this.toMillimetres(width);
      const minOffcut = this.config.minOffcut || 100;

      if (lengthMm < minOffcut || widthMm < minOffcut) {
        this.showError('Each side needs to be at least ' + minOffcut + ' mm—smaller pieces cannot be cut from a sheet.');
        return;
      }

      if (lengthMm > MAX_SIDE_MM || widthMm > MAX_SIDE_MM) {
        this.showError('That floor is larger than we can price online. Please contact us for a quote.', true);
        return;
      }

      const floorArea = lengthMm * widthMm;
      const allowance = 1 + (this.config.wasteAllowance || 0) / 100;
      this.plan = planSheets(floorArea * allowance, this.availableSheets);
      this.render(floorArea);
    }

    setButtonLabel(label) {
      if (!this.addButton) return;
      this.addButton.textContent = label;
    }

    setButtonEnabled(enabled) {
      if (!this.addButton) return;
      this.addButton.disabled = !enabled;
      if (!enabled) this.setButtonLabel(this.defaultButtonLabel);
    }

    render(floorArea) {
      const moneyFormat = this.config.moneyFormat;
      const sheetCount = this.plan.reduce((total, line) => total + line.quantity, 0);
      const material = this.plan.reduce((total, line) => total + line.sheet.area * line.quantity, 0);
      const totalPrice = this.plan.reduce((total, line) => total + line.sheet.price * line.quantity, 0);
      const countLabel = sheetCount + (sheetCount === 1 ? ' sheet' : ' sheets');

      if (this.headline) this.headline.textContent = countLabel;

      if (this.linesEl) {
        this.linesEl.innerHTML = '';
        this.plan.forEach((line) => {
          const item = document.createElement('li');
          item.className = this.compact ? 'cbp-calc__line' : 'cbc__line';

          const name = document.createElement('span');
          name.className = this.compact ? 'cbp-calc__line-name' : 'cbc__line-name';
          name.textContent = this.compact
            ? line.quantity + ' × ' + line.sheet.label
            : line.sheet.label;

          if (!this.compact) {
            const qty = document.createElement('span');
            qty.className = 'cbc__line-qty';
            qty.textContent = line.quantity + ' × ' + formatMoney(line.sheet.price, moneyFormat);
            name.appendChild(qty);
          }

          const price = document.createElement('span');
          price.className = this.compact ? 'cbp-calc__line-price' : 'cbc__line-price';
          price.textContent = formatMoney(line.sheet.price * line.quantity, moneyFormat);

          item.appendChild(name);
          item.appendChild(price);
          this.linesEl.appendChild(item);
        });
      }

      if (this.totalEl) this.totalEl.textContent = formatMoney(totalPrice, moneyFormat);

      if (this.metaEl) {
        this.metaEl.textContent = this.compact
          ? 'Covers ' + formatArea(floorArea)
          : 'Covers ' +
            formatArea(floorArea) +
            ' of floor · ' +
            formatArea(material) +
            ' of material · ' +
            formatArea(Math.max(0, material - floorArea)) +
            ' left for offcuts and trims.';
      }

      if (this.placeholder) this.placeholder.hidden = true;
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.summary) this.summary.hidden = false;
      this.setButtonEnabled(true);
      if (this.addButton && this.addButton.dataset.cbcAddLabel) {
        this.setButtonLabel(this.addButton.dataset.cbcAddLabel.replace('{count}', countLabel));
      } else if (this.compact) {
        this.setButtonLabel('Add ' + countLabel + ' to cart');
      }
      this.hideFeedback();
    }

    showPlaceholder() {
      this.plan = [];
      if (this.placeholder) this.placeholder.hidden = false;
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.summary) this.summary.hidden = true;
      this.setButtonEnabled(false);
      this.hideFeedback();
    }

    showError(message, withHelpLink) {
      this.plan = [];
      if (this.errorEl) {
        this.errorEl.textContent = message + ' ';
        if (withHelpLink && this.config.helpUrl) {
          const link = document.createElement('a');
          link.className = this.compact ? 'cbp-calc__link' : 'cbc__link';
          link.href = this.config.helpUrl;
          link.textContent = 'Get in touch';
          this.errorEl.appendChild(link);
        }
        this.errorEl.hidden = false;
      }
      if (this.placeholder) this.placeholder.hidden = true;
      if (this.summary) this.summary.hidden = true;
      this.setButtonEnabled(false);
      this.hideFeedback();
    }

    hideFeedback() {
      if (!this.feedback) return;
      this.feedback.hidden = true;
      this.feedback.textContent = '';
    }

    showFeedback(message, cartLink) {
      if (!this.feedback) return;
      this.feedback.textContent = message + ' ';
      if (cartLink) {
        const link = document.createElement('a');
        link.className = this.compact ? 'cbp-calc__link' : 'cbc__link';
        link.href = this.config.cartUrl || '/cart';
        link.textContent = 'View cart';
        this.feedback.appendChild(link);
      }
      this.feedback.hidden = false;
    }

    async addToCart() {
      if (!this.plan.length) return;

      const items = this.plan.map((line) => ({ id: line.sheet.id, quantity: line.quantity }));
      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items: items };

      if (cartDrawer) {
        body.sections = ['cart-drawer', 'cart-icon-bubble'];
        body.sections_url = window.location.pathname;
      }

      this.addButton.disabled = true;
      this.hideFeedback();

      try {
        const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const state = await response.json();

        if (!response.ok) {
          this.showFeedback(state.description || state.message || 'Those sheets could not be added to your cart.');
          return;
        }

        if (cartDrawer && state.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(state);
        } else {
          window.location = this.config.cartUrl || '/cart';
          return;
        }

        this.showFeedback('Added to your cart.', true);
      } catch (error) {
        this.showFeedback('Something went wrong adding to your cart. Please try again.');
      } finally {
        this.addButton.disabled = false;
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-cbc-root]').forEach((root) => {
      if (root.dataset.cbcReady) return;
      root.dataset.cbcReady = 'true';
      new SheetCalculator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('product-info:loaded', (event) => init(event.target));
})();
